import { create } from "zustand";
import type { CelestialObject, StarSystem, ViewMode, ViewState } from "../types/celestial";

// --- Scale limits ---
const GALAXY_MIN_SCALE = 0.3;
const GALAXY_MAX_SCALE = 10;
const SYSTEM_MIN_SCALE = 1;
const SYSTEM_MAX_SCALE = 4;
const SYSTEM_PAN_LIMIT = 200;

const INITIAL_GALAXY_VIEW: ViewState = {
	offsetX: typeof window !== "undefined" ? window.innerWidth / 2 : 640,
	offsetY: typeof window !== "undefined" ? window.innerHeight / 2 : 480,
	scale: 1,
};

const INITIAL_SYSTEM_VIEW: ViewState = {
	offsetX: typeof window !== "undefined" ? window.innerWidth / 2 : 640,
	offsetY: typeof window !== "undefined" ? window.innerHeight / 2 : 480,
	scale: 3,
};

function clampSystemOffset(view: ViewState): void {
	const cx = typeof window !== "undefined" ? window.innerWidth / 2 : 640;
	const cy = typeof window !== "undefined" ? window.innerHeight / 2 : 480;
	const limit = SYSTEM_PAN_LIMIT * view.scale;
	view.offsetX = Math.max(cx - limit, Math.min(cx + limit, view.offsetX));
	view.offsetY = Math.max(cy - limit, Math.min(cy + limit, view.offsetY));
}

// --- Agent observation types ---

export interface AgentState {
	id: string;
	name: string;
	credits: number;
	locationPlanetId: string;
}

export interface TravelState {
	agentId: string;
	agentName: string;
	fromPlanetId: string;
	toPlanetId: string;
	departedAt: number;
	arrivesAt: number;
}

export interface TradeEvent {
	id: string;
	agentId: string;
	agentName: string;
	planetId: string;
	good: string;
	action: "buy" | "sell";
	quantity: number;
	unitPrice: number;
	timestamp: number;
	fadeOut: number;
}

export interface GalacticEventState {
	id: string;
	eventType: string;
	good: string;
	zoneMin: number;
	zoneMax: number;
	multiplier: number;
	description: string;
	expiresAt: number;
}

export interface GameEventLog {
	type: string;
	message: string;
	timestamp: number;
}

// --- Store interface ---

interface ObserverState {
	// Map state
	systems: StarSystem[];
	viewMode: ViewMode;
	focusedSystem: StarSystem | null;
	galaxySavedView: ViewState | null;
	galaxyView: ViewState;
	systemView: ViewState;
	selectedObject: CelestialObject | null;
	hoveredObject: CelestialObject | null;
	hasEnteredGalaxy: boolean;
	isSearchOpen: boolean;
	isSidebarOpen: boolean;

	// Agent observation state
	agents: Map<string, AgentState>;
	activeTravels: TravelState[];
	recentTrades: TradeEvent[];
	galacticEvents: GalacticEventState[];
	eventLog: GameEventLog[];
	connected: boolean;

	// Map actions
	setSystems: (systems: StarSystem[]) => void;
	setView: (view: Partial<ViewState>) => void;
	pan: (dx: number, dy: number) => void;
	zoom: (factor: number, centerX?: number, centerY?: number) => void;
	resetView: () => void;
	enterSystemView: (system: StarSystem) => void;
	exitSystemView: () => void;
	selectObject: (obj: CelestialObject | null) => void;
	hoverObject: (obj: CelestialObject | null) => void;
	setHasEnteredGalaxy: (val: boolean) => void;

	// Agent actions
	applySnapshot: (agents: AgentState[], travels: TravelState[], galacticEvents?: GalacticEventState[]) => void;
	addAgent: (agent: AgentState) => void;
	updateAgentLocation: (agentId: string, planetId: string) => void;
	addTravel: (travel: TravelState) => void;
	removeTravel: (agentId: string) => void;
	addTrade: (trade: TradeEvent) => void;
	pruneOldTrades: () => void;
	addEvent: (log: GameEventLog) => void;
	addGalacticEvent: (event: GalacticEventState) => void;
	removeExpiredGalacticEvents: () => void;
	setConnected: (v: boolean) => void;
}

export const useObserverStore = create<ObserverState>((set, get) => ({
	// Map state
	systems: [],
	viewMode: "galaxy",
	focusedSystem: null,
	galaxySavedView: null,
	galaxyView: INITIAL_GALAXY_VIEW,
	systemView: INITIAL_SYSTEM_VIEW,
	selectedObject: null,
	hoveredObject: null,
	hasEnteredGalaxy: false,
	isSearchOpen: false,
	isSidebarOpen: false,

	// Agent state
	agents: new Map(),
	activeTravels: [],
	recentTrades: [],
	galacticEvents: [],
	eventLog: [],
	connected: false,

	// Map actions
	setSystems: (systems) => set({ systems }),

	selectObject: (obj) => set({ selectedObject: obj, isSidebarOpen: obj !== null }),

	hoverObject: (obj) => set({ hoveredObject: obj }),

	setView: (partial) =>
		set((state) => {
			const key = state.viewMode === "galaxy" ? "galaxyView" : "systemView";
			const merged = { ...state[key], ...partial };
			if (state.viewMode === "system") clampSystemOffset(merged);
			return { [key]: merged };
		}),

	pan: (dx, dy) =>
		set((state) => {
			const key = state.viewMode === "galaxy" ? "galaxyView" : "systemView";
			const view = {
				...state[key],
				offsetX: state[key].offsetX + dx,
				offsetY: state[key].offsetY + dy,
			};
			if (state.viewMode === "system") clampSystemOffset(view);
			return { [key]: view };
		}),

	zoom: (factor, centerX, centerY) => {
		const state = get();
		const key = state.viewMode === "galaxy" ? "galaxyView" : "systemView";
		const current = state[key];
		const [minS, maxS] =
			state.viewMode === "galaxy"
				? [GALAXY_MIN_SCALE, GALAXY_MAX_SCALE]
				: [SYSTEM_MIN_SCALE, SYSTEM_MAX_SCALE];
		const newScale = Math.max(minS, Math.min(maxS, current.scale * factor));

		if (centerX !== undefined && centerY !== undefined) {
			const scaleRatio = newScale / current.scale;
			const view = {
				offsetX: centerX - (centerX - current.offsetX) * scaleRatio,
				offsetY: centerY - (centerY - current.offsetY) * scaleRatio,
				scale: newScale,
			};
			if (state.viewMode === "system") clampSystemOffset(view);
			set({ [key]: view });
		} else {
			set({ [key]: { ...current, scale: newScale } });
		}
	},

	resetView: () =>
		set((state) => {
			if (state.viewMode === "galaxy") return { galaxyView: INITIAL_GALAXY_VIEW };
			return { systemView: INITIAL_SYSTEM_VIEW };
		}),

	enterSystemView: (system) =>
		set((state) => ({
			viewMode: "system" as const,
			focusedSystem: system,
			galaxySavedView: { ...state.galaxyView },
			systemView: {
				offsetX: typeof window !== "undefined" ? window.innerWidth / 2 : 640,
				offsetY: typeof window !== "undefined" ? window.innerHeight / 2 : 480,
				scale: 3,
			},
			selectedObject: null,
			isSidebarOpen: false,
		})),

	exitSystemView: () =>
		set((state) => ({
			viewMode: "galaxy" as const,
			focusedSystem: null,
			galaxyView: state.galaxySavedView ?? INITIAL_GALAXY_VIEW,
			galaxySavedView: null,
			selectedObject: null,
			isSidebarOpen: false,
		})),

	setHasEnteredGalaxy: (val) => set({ hasEnteredGalaxy: val }),

	// Agent actions
	applySnapshot: (agentsArr, travels, galEvts) => {
		const map = new Map<string, AgentState>();
		for (const a of agentsArr) {
			map.set(a.id, a);
		}
		set({ agents: map, activeTravels: travels, galacticEvents: galEvts ?? [] });
	},

	addAgent: (agent) =>
		set((state) => {
			const next = new Map(state.agents);
			next.set(agent.id, agent);
			return { agents: next };
		}),

	updateAgentLocation: (agentId, planetId) =>
		set((state) => {
			const agent = state.agents.get(agentId);
			if (!agent) return {};
			const next = new Map(state.agents);
			next.set(agentId, { ...agent, locationPlanetId: planetId });
			return { agents: next };
		}),

	addTravel: (travel) =>
		set((state) => ({
			activeTravels: [...state.activeTravels, travel],
		})),

	removeTravel: (agentId) =>
		set((state) => ({
			activeTravels: state.activeTravels.filter((t) => t.agentId !== agentId),
		})),

	addTrade: (trade) =>
		set((state) => ({
			recentTrades: [...state.recentTrades, trade],
		})),

	pruneOldTrades: () =>
		set((state) => ({
			recentTrades: state.recentTrades.filter((t) => Date.now() < t.fadeOut),
		})),

	addEvent: (log) =>
		set((state) => {
			const next = [...state.eventLog, log];
			if (next.length > 100) next.shift();
			return { eventLog: next };
		}),

	addGalacticEvent: (event) =>
		set((state) => {
			if (state.galacticEvents.some((e) => e.id === event.id)) return {};
			return { galacticEvents: [...state.galacticEvents, event] };
		}),

	removeExpiredGalacticEvents: () =>
		set((state) => ({
			galacticEvents: state.galacticEvents.filter((e) => Date.now() < e.expiresAt),
		})),

	setConnected: (v) => set({ connected: v }),
}));
